// src/components/ServerDataExtraction/ExtractionResults.jsx
import React from 'react';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion'; // Import motion
import { parseMinecraftFormatting } from '../../utils/minecraftFormatting'; // Import MOTD parser

// Accept serverData (which could be basic or detailed), extractionStatus, isLoading (for extraction process), isDisabled (for connection state)
// Added extractionOptions and targetPort as props
const ExtractionResults = ({ serverData, extractionStatus, isLoading, isDisabled, extractionOptions, targetPort }) => {
  const [activeTab, setActiveTab] = useState('info'); // Default to 'info'
  const displayData = serverData || {}; // Use serverData as the source

  // Reset tab if data becomes unavailable (e.g., disconnect)
  useEffect(() => {
      if (isDisabled || !serverData) {
          setActiveTab('info'); // Reset to a default tab
      }
  }, [isDisabled, serverData]);


  // Helper to render placeholder text
  const Placeholder = ({ text = "N/A" }) => <span className="text-gray-500 italic">{text}</span>;

  // Determine main display state
  let mainContent;
  if (isLoading && extractionStatus === 'extracting') {
     // --- Specific Loading State during Extraction ---
     mainContent = (
          <div className="flex-1 flex items-center justify-center text-center min-h-[300px]">
              <div>
                  <i className="fas fa-spinner text-3xl text-accent-blue animate-spin mb-3"></i>
                  <h3 className="text-md font-medium text-gray-300">Extracting Data...</h3>
                  <p className="text-xs text-gray-500 mt-1">Please wait.</p>
              </div>
          </div>
     );
  } /*else if (isDisabled || !serverData) {
       // --- Disabled/Not Connected State ---
      mainContent = (
          <div className="flex-1 flex items-center justify-center text-center text-gray-500 min-h-[300px]">
              <div>
                  <i className="fas fa-plug text-3xl mb-3"></i>
                  <p>Connect to a server to view extracted data.</p>
              </div>
          </div>
      );
  }*/ else {
// --- Data Display State (Idle, Complete, Partial, Error during extraction) ---
      const hasDetailedData = extractionStatus === 'complete' || extractionStatus === 'partial';
      const currentExtractionOptions = extractionOptions || {}; // Ensure extractionOptions is defined

      // Define tab structure - show tabs based on potentially available data keys
      const tabs = [
          { key: 'info', label: 'Info', icon: 'fa-info-circle', color: 'blue' },
          { key: 'icon', label: 'Icon', icon: 'fa-image', color: 'yellow' },
          { key: 'players', label: 'Players', icon: 'fa-users', color: 'green' },
          { key: 'plugins', label: 'Plugins', icon: 'fa-puzzle-piece', color: 'purple' }, // Plugins data not in current DTO, tab shown always
          { key: 'motd', label: 'MOTD', icon: 'fa-quote-right', color: 'yellow' },
          { key: 'raw', label: 'Raw', icon: 'fa-code', color: 'purple' } // Always show raw
      ];
       // Helper function to safely render MOTD from various formats
       // Helper function to safely render MOTD from various formats
        const renderMotdHtml = (motd) => {
            if (!motd) return null;
            if (motd.html) return motd.html;
            if (motd.clean) return motd.clean.replace(/\n/g, '<br>');
            if (typeof motd === 'string') return motd.replace(/§[0-9a-fk-or]/g, '').replace(/\n/g, '<br>'); // Basic stripping + newlines
            return typeof motd === 'object' ? JSON.stringify(motd) : String(motd); // Fallback
        };

      // Helper function to get the appropriate MOTD string for display parsing
      const getDisplayMotdString = (motdData) => {
        if (!motdData) return null;
        if (typeof motdData === 'object') {
          // Prefer .raw for § formatting codes, then .clean as fallback
          if (typeof motdData.raw === 'string') return motdData.raw;
          if (typeof motdData.clean === 'string') return motdData.clean;
          return null; // No suitable string found in object
        }
        if (typeof motdData === 'string') {
          return motdData;
        }
        return null; // Not a recognized MOTD format
      };

      // Helper function to get the MOTD string for the "Copy Raw" button
      const getMotdForCopy = (motdData) => {
        if (!motdData) return "";
        if (typeof motdData === 'object') {
          if (typeof motdData.raw === 'string') return motdData.raw; // Primary target for "raw"
          if (typeof motdData.clean === 'string') return motdData.clean; // Fallback
          return JSON.stringify(motdData); // Fallback for unusual object
        }
        if (typeof motdData === 'string') {
          return motdData;
        }
        return String(motdData); // Last resort
      };

      const motdStringForDisplay = getDisplayMotdString(displayData.motd);

      mainContent = (
          <>
              {/* Tabs Navigation */}
              <div className={`border-b border-dark-400 mb-4 ${isDisabled ? 'pointer-events-none' : ''}`}>
                  <nav className="flex space-x-1 sm:space-x-2 overflow-x-auto custom-scrollbar pb-px">
                       {tabs.map(tab => ( // Filter tabs based on condition
                          <button
                              key={tab.key}
                              className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                              activeTab === tab.key
                                  ? `text-accent-${tab.color} border-b-2 border-accent-${tab.color}`
                                  : 'text-gray-400 hover:text-gray-200 border-b-2 border-transparent'
                              }`}
                              onClick={() => setActiveTab(tab.key)}
                              disabled={isDisabled}
                          >
                            <i className={`fas ${tab.icon} mr-1 hidden sm:inline-block`}></i>
                            {tab.label}
                          </button>
                       ))}
                  </nav>
              </div>

              {/* Tab Content Area */}
              <div className="flex-1 overflow-y-auto space-y-4 custom-scrollbar pr-1 text-sm">
                    {/* --- Render content based on activeTab --- */}


                    {/* Server Icon */}
                    {(activeTab === 'all' || activeTab === 'icon') && (
                        <motion.div initial={{opacity:0}} animate={{opacity:1}} className="bg-dark-500 rounded-xl p-4">
                            <div className="flex justify-between items-center mb-3">
                            <h4 className="text-sm font-semibold flex items-center"><i className="fas fa-image text-accent-yellow mr-2"></i> Server Icon</h4>
                            <button
                                onClick={() => downloadData(displayData.icon, 'server_icon.png', 'image/png', true)}
                                className={`text-xs px-2 py-1 bg-dark-400 rounded transition ${isDisabled || !displayData.icon ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-300'}`}
                                disabled={isDisabled || !displayData.icon}
                            > <i className="fas fa-download mr-1"></i> Download </button>
                            </div>
                            <div className="flex items-center justify-center bg-dark-600 rounded-lg p-4 min-h-[100px]">
                            {displayData.icon && !isDisabled ? (
                                <div className="w-24 h-24 bg-dark-400 rounded-lg overflow-hidden flex items-center justify-center">
                                    {/* displayData.icon should now be a full data URI from backend */}
                                    <img src={displayData.icon} alt="Server Icon" className="max-w-full max-h-full object-contain pixelated" onError={(e) => e.target.style.display='none'} />
                                </div>
                            ) : (
                                <Placeholder text={extractionStatus === 'error' ? "Error loading icon" : "Icon N/A"} />
                            )}
                            </div>
                        </motion.div>
                    )}

                    {/* Server Info */}
                    {(activeTab === 'all' || activeTab === 'info') && (
                        <motion.div initial={{opacity:0}} animate={{opacity:1}} className="bg-dark-500 rounded-xl p-4">
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="text-sm font-semibold flex items-center"><i className="fas fa-info-circle text-accent-blue mr-2"></i> Server Information</h4>
                                <button
                                    onClick={() => copyToClipboard(formatInfoDataForCopy(displayData, targetPort))}
                                    className={`text-xs px-2 py-1 bg-dark-400 rounded transition ${isDisabled || !displayData.success ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-300'}`}
                                    disabled={isDisabled || !displayData.success}
                                ><i className="fas fa-copy mr-1"></i> Copy</button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-xs">
                                {[
                                    // Adjusted to use fields from ServerDataExtractionResultDto
                                    { label: 'IP Address', value: displayData?.ip },
                                    { label: 'Port', value: targetPort || 'N/A' }, // Use targetPort prop
                                    { label: 'Status', value: displayData?.success, isStatus: true }, // Infer status from DTO's success field
                                    { label: 'Version', value: displayData?.version },
                                    { label: 'Players', value: (displayData?.onlinePlayers !== undefined && displayData?.maxPlayers !== undefined) ? `${displayData.onlinePlayers?.toLocaleString()} / ${displayData.maxPlayers?.toLocaleString()}` : null },
                                    { label: 'Ping', value: typeof displayData?.ping === 'number' ? `${displayData.ping}ms` : displayData?.ping }
                                ].map((field) => (
                                    <div key={field.label} className="flex justify-between py-1 border-b border-dark-600 last:border-b-0">
                                        <span className="text-gray-400 mr-2">{field.label}:</span>
                                        {field.isStatus ? (
                                            <span className={field.value ? 'text-accent-green font-medium' : 'text-accent-red font-medium'}>
                                                {field.value ? 'Online' : (extractionStatus === 'error' ? 'Error' : 'Offline')}
                                            </span>
                                        ) : (
                                            field.value !== null && field.value !== undefined ? <span className="text-gray-200 text-right truncate">{String(field.value)}</span> : <Placeholder />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {/* Player List */}
                    {(activeTab === 'all' || activeTab === 'players') && (
                        <motion.div initial={{opacity:0}} animate={{opacity:1}} className="bg-dark-500 rounded-xl p-4">
                            <div className="flex justify-between items-center mb-3">
<h4 className="text-sm font-semibold flex items-center">
                                    <i className="fas fa-users text-accent-green mr-2"></i> Player List
                                    {(hasDetailedData && displayData.onlinePlayers !== undefined) && ( // Use onlinePlayers from DTO
                                        <span className="ml-2 text-xs py-0.5 px-1.5 bg-dark-400 rounded text-gray-300">{displayData.onlinePlayers?.toLocaleString() ?? '0'} online</span>
                                    )}
                                </h4>
                                <div className="flex space-x-2">
                                    <button onClick={() => copyToClipboard(displayData.players?.join('\n') || '')} className={`text-xs px-2 py-1 bg-dark-400 rounded transition ${isDisabled || !hasDetailedData || !displayData.players?.length ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-300'}`} disabled={isDisabled || !hasDetailedData || !displayData.players?.length}><i className="fas fa-copy mr-1"></i> Copy</button>
                                    <button onClick={() => downloadData(displayData.players?.join('\n') || '', 'player_list.txt', 'text/plain')} className={`text-xs px-2 py-1 bg-dark-400 rounded transition ${isDisabled || !hasDetailedData || !displayData.players?.length ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-300'}`} disabled={isDisabled || !hasDetailedData || !displayData.players?.length}><i className="fas fa-download mr-1"></i> Download</button>
                                </div>
                            </div>
                            <div className="bg-dark-600 rounded-lg p-3 min-h-[100px] max-h-[200px] overflow-y-auto custom-scrollbar">
                                {hasDetailedData && displayData.players?.length > 0 ? ( // displayData.players is the list itself
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                        {displayData.players.map((player, index) => (
                                        <div key={index} className="bg-dark-500 rounded p-2 flex items-center text-xs truncate hover:bg-dark-400 transition">
                                            <img src={`https://minotar.net/avatar/${player}/24`} alt="" className="w-5 h-5 rounded mr-2 pixelated" onError={(e) => { e.target.onerror = null; e.target.src = 'https://minotar.net/avatar/Steve/24'; }}/>
                                            <span className="truncate">{player}</span>
                                        </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-full">
                                        <Placeholder text={extractionStatus === 'error' ? "Error loading players" : "No player data"} />
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {/* Plugin List */}
                    {(activeTab === 'all' || activeTab === 'plugins') && (
                        <motion.div initial={{opacity:0}} animate={{opacity:1}} className="bg-dark-500 rounded-xl p-4">
                            <div className="flex justify-between items-center mb-3">
<h4 className="text-sm font-semibold flex items-center">
                                    <i className="fas fa-puzzle-piece text-accent-purple mr-2"></i> Plugin List
                                    {/* Plugin data is not in ServerDataExtractionResultDto. This will show count if plugins were added to DTO and displayData.plugins existed. */}
                                    {(hasDetailedData && displayData.plugins?.length > 0) && (
                                        <span className="ml-2 text-xs py-0.5 px-1.5 bg-dark-400 rounded text-gray-300">{displayData.plugins.length} detected</span>
                                    )}
                                </h4>
                                <div className="flex space-x-2">
                                <button onClick={() => copyToClipboard(displayData.plugins?.map(p => `${p.name}${p.version ? ` (${p.version})` : ''}`).join('\n') || '')} className={`text-xs px-2 py-1 bg-dark-400 rounded transition ${isDisabled || !hasDetailedData || !displayData.plugins?.length ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-300'}`} disabled={isDisabled || !hasDetailedData || !displayData.plugins?.length}><i className="fas fa-copy mr-1"></i> Copy</button>
                                <button onClick={() => downloadData(displayData.plugins?.map(p => `${p.name}${p.version ? ` (${p.version})` : ''}`).join('\n') || '', 'plugin_list.txt', 'text/plain')} className={`text-xs px-2 py-1 bg-dark-400 rounded transition ${isDisabled || !hasDetailedData || !displayData.plugins?.length ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-300'}`} disabled={isDisabled || !hasDetailedData || !displayData.plugins?.length}><i className="fas fa-download mr-1"></i> Download</button>
                                </div>
                            </div>
                            <div className="bg-dark-600 rounded-lg p-3 min-h-[100px] max-h-[200px] overflow-y-auto custom-scrollbar">
                                {hasDetailedData && displayData.plugins?.length > 0 ? ( // Access displayData.plugins
                                    <ul className="space-y-1">
                                        {displayData.plugins.map((plugin, index) => ( // Iterate over displayData.plugins
                                            <li key={index} className="bg-dark-500 rounded px-2 py-1 flex justify-between items-center text-xs hover:bg-dark-400 transition">
                                                <span className="truncate">{plugin.name || 'Unknown Plugin'}</span>
                                                {plugin.version && <span className="text-gray-400 ml-2 flex-shrink-0">{plugin.version}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="flex items-center justify-center h-full">
                                        <Placeholder text={extractionStatus === 'error' ? "Error loading plugins" : "No plugins detected"} />
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {/* MOTD Preview */}
                    {(activeTab === 'all' || activeTab === 'motd') && (
                        <motion.div initial={{opacity:0}} animate={{opacity:1}} className="bg-dark-500 rounded-xl p-4">
<div className="flex justify-between items-center mb-3">
                                <h4 className="text-sm font-semibold flex items-center"><i className="fas fa-quote-right text-accent-yellow mr-2"></i> MOTD</h4>
                                <button onClick={() => copyToClipboard(getMotdForCopy(displayData.motd))} className={`text-xs px-2 py-1 bg-dark-400 rounded transition ${isDisabled || !displayData.motd ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-300'}`} disabled={isDisabled || !displayData.motd}><i className="fas fa-copy mr-1"></i> Copy Raw</button>
                            </div>
                            <div className="bg-dark-600 rounded-lg p-4 min-h-[80px]">
                                {motdStringForDisplay !== null ? (
                                    // Use parseMinecraftFormatting utility with the extracted string
                                    <div className="font-minecraft text-sm leading-normal whitespace-pre-wrap">
                                        {parseMinecraftFormatting(motdStringForDisplay)}
                                    </div>
                                ) : (
                                    <Placeholder text={extractionStatus === 'error' ? "Error loading MOTD" : "MOTD N/A"} />
                                )}
                            </div>
                        </motion.div>
                    )}

                    {/* Raw Data */}
                    {(activeTab === 'all' || activeTab === 'raw') && (
                        <motion.div initial={{opacity:0}} animate={{opacity:1}} className="bg-dark-500 rounded-xl p-4">
<div className="flex justify-between items-center mb-3">
                            <h4 className="text-sm font-semibold flex items-center"><i className="fas fa-code text-accent-purple mr-2"></i> Raw Data (JSON)</h4>
                            <div className="flex space-x-2">
                                <button onClick={() => copyToClipboard(JSON.stringify(displayData, null, 2))} className={`text-xs px-2 py-1 bg-dark-400 rounded transition ${isDisabled || !Object.keys(displayData).length ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-300'}`} disabled={isDisabled || !Object.keys(displayData).length}><i className="fas fa-copy mr-1"></i> Copy</button>
                                <button onClick={() => downloadData(JSON.stringify(displayData, null, 2), 'raw_server_data.json', 'application/json')} className={`text-xs px-2 py-1 bg-dark-400 rounded transition ${isDisabled || !Object.keys(displayData).length ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-300'}`} disabled={isDisabled || !Object.keys(displayData).length}><i className="fas fa-download mr-1"></i> Download</button>
                                </div>
                            </div>
                            <div className="terminal text-xs overflow-auto bg-dark-700 rounded p-3 max-h-[250px] custom-scrollbar">
                                {displayData ? (
                                    <pre>{JSON.stringify(displayData, null, 2)}</pre>
                                ) : (
                                    <Placeholder text={"// No data available"} />
                                )}
                            </div>
                        </motion.div>
                    )}

              </div>
          </>
      );
  }


  return (
      // Main container div for the results panel
      <div className={`bg-dark-600 rounded-2xl p-4 sm:p-6 h-full flex flex-col border border-dark-400 hover:border-accent-blue/30 transition ${isDisabled ? 'opacity-60' : ''}`}>
          {/* Header */}
          <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h3 className="font-bold text-base sm:text-lg flex items-center">
                  <i className="fas fa-database mr-2 text-accent-blue"></i>
                  Extracted Data
                   {/* Status Badge */}
                    {!isDisabled && serverData && (
                        <span className={`ml-2 sm:ml-3 text-xs py-1 px-2 rounded-lg ${
                            extractionStatus === 'complete' ? 'bg-accent-green/20 text-accent-green' :
                            extractionStatus === 'partial' ? 'bg-accent-yellow/20 text-accent-yellow' :
                            extractionStatus === 'error' ? 'bg-accent-red/20 text-accent-red' :
                            extractionStatus === 'extracting' ? 'bg-accent-blue/20 text-accent-blue animate-pulse' :
                            'bg-dark-500 text-gray-400' // Idle
                        }`}>
                            {extractionStatus.charAt(0).toUpperCase() + extractionStatus.slice(1)}
                        </span>
                    )}
              </h3>
              {/* Action buttons - Conditionally render or disable */}
              {!isDisabled && serverData && (
<div className="flex items-center space-x-2">
                      <button
                          onClick={() => downloadData(JSON.stringify(displayData, null, 2), 'extracted_server_data.json', 'application/json')}
                          className={`bg-dark-500 hover:bg-dark-400 px-2 py-1 rounded-lg text-xs transition flex items-center ${isDisabled || !displayData.success || extractionStatus === 'extracting' ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={isDisabled || !displayData.success || extractionStatus === 'extracting'}
                          title="Download All Data (JSON)"
                      >
                          <i className="fas fa-file-download"></i> <span className="hidden sm:inline ml-1">Download</span>
                      </button>
                      {/* Add Copy All button if needed */}
                  </div>
              )}
          </div>

           {/* Render the determined main content */}
           {mainContent}

</div>
  );
};

const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
        // Optional: Show a success notification
        console.log('Copied to clipboard');
    }).catch(err => {
        console.error('Failed to copy: ', err);
    });
};

const downloadData = (data, filename, type, isDataUrl = false) => {
    if (!data) return;
    const link = document.createElement('a');
    if (isDataUrl) {
        link.href = data;
    } else {
        const blob = new Blob([data], { type: type || 'text/plain' });
        link.href = URL.createObjectURL(blob);
    }
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (!isDataUrl) {
        URL.revokeObjectURL(link.href);
    }
};

const formatInfoDataForCopy = (data, port) => {
    if (!data || !data.success) return "No data to copy.";
    return `IP Address: ${data.ip || 'N/A'}
Port: ${port || 'N/A'}
Status: ${data.success ? 'Online' : 'Offline'}
Version: ${data.version || 'N/A'}
Players: ${(data.onlinePlayers !== undefined && data.maxPlayers !== undefined) ? `${data.onlinePlayers}/${data.maxPlayers}` : 'N/A'}
Ping: ${data.ping !== undefined ? `${data.ping}ms` : 'N/A'}`;
};

export default ExtractionResults;